// Where was this diary entry written about?
//
// The common case is NOT standing at the place: a traveller writes the entry
// at home, days later. So this offers four ways to set a location, in the
// order of how much they can be trusted, and every one of them is optional.
//
//   1. EXIF GPS from an uploaded photo. Handled by CaptureScreen before this
//      component is even shown, because the photo was literally taken there.
//   2. Drop a pin on the map. BUILT FIRST and always present: it needs no
//      network, no provider and no typing, so it is the one path that cannot
//      fail. Everything else is an enhancement on top of it.
//   3. Address search, through the API's geocoder. Searched on submit, never
//      per keystroke: the provider's terms forbid autocomplete querying.
//   4. Reuse a destination other travellers already posted at, which is how
//      an entry joins an existing place instead of starting a new one.
//
// A location is never required. An entry with none saves, appears in the
// diary and the timeline, and simply does not get a map pin.
import { useEffect, useMemo, useState } from "react";
import { Icon, IconCheck, IconGlobe, IconMap, IconPin, IconSend } from "@traveldiary/ui";
import { MapView } from "./MapView";
import { Segmented } from "./Segmented";
import { apiClient } from "../lib/api";
import { formatRelative } from "../lib/format";
import { INDONESIA_BOUNDS } from "../lib/config";
import type { GeocodeHit, Place } from "../lib/types";
import { useApp } from "../state/app";

export type PickedLocation = {
  lat: number;
  lng: number;
  /** Suggested place name, only when the source supplied one. */
  placeName?: string | null;
};

export interface LocationPickerProps {
  value: { lat: number; lng: number } | null;
  onChange(picked: PickedLocation | null): void;
}

type Mode = "map" | "search" | "places";

export function LocationPicker({ value, onChange }: LocationPickerProps) {
  const { t, locale } = useApp();
  const [mode, setMode] = useState<Mode>("map");
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<GeocodeHit[] | null>(null);
  const [attribution, setAttribution] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [placeQuery, setPlaceQuery] = useState("");

  useEffect(() => {
    if (mode !== "places" || places !== null) return;
    void apiClient
      .listPlaces(INDONESIA_BOUNDS)
      .then((list) => setPlaces(list ?? []))
      .catch(() => setPlaces([]));
  }, [mode, places]);

  const filteredPlaces = useMemo(() => {
    const needle = placeQuery.trim().toLowerCase();
    const list = places ?? [];
    if (!needle) return list.slice(0, 20);
    return list
      .filter((place) => (place.placeName ?? "").toLowerCase().includes(needle))
      .slice(0, 20);
  }, [places, placeQuery]);

  async function runSearch(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 3 || searching) return;
    setSearching(true);
    setSearchFailed(false);
    try {
      const result = await apiClient.geocode(trimmed);
      setHits(result.results);
      setAttribution(result.attribution);
    } catch {
      setHits([]);
      setSearchFailed(true);
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="picker">
      <Segmented<Mode>
        label={t("capture.locationHow")}
        value={mode}
        onChange={setMode}
        options={[
          { value: "map", label: t("capture.locationMap") },
          { value: "search", label: t("capture.locationSearch") },
          { value: "places", label: t("capture.locationPlaces") },
        ]}
      />

      {mode === "map" ? (
        <div className="picker-map">
          <MapView
            basemap="street"
            dimension="2d"
            places={[]}
            user={null}
            follow={false}
            recenterNonce={0}
            pickMode
            pickedPoint={value}
            onPickPoint={(point) => onChange({ lat: point.lat, lng: point.lng })}
            onError={() => {}}
          />
        </div>
      ) : null}

      {mode === "search" ? (
        <div className="picker-panel">
          <form className="comment-form" onSubmit={runSearch}>
            <input
              className="input"
              name="address"
              value={query}
              placeholder={t("capture.locationSearchPlaceholder")}
              onChange={(event) => setQuery(event.target.value)}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={searching || query.trim().length < 3}
              aria-label={t("capture.locationSearchGo")}
            >
              <Icon size={18}>
                <IconSend />
              </Icon>
            </button>
          </form>
          <p className="field-note">{t("capture.locationSearchHint")}</p>
          {searchFailed ? (
            <p className="field-note" role="status">
              {t("capture.locationSearchFailed")}
            </p>
          ) : null}
          <ul className="who-list">
            {(hits ?? []).map((hit) => (
              <li key={`${hit.label}:${hit.lat},${hit.lng}`}>
                <button
                  type="button"
                  className="who-row"
                  onClick={() => onChange({ lat: hit.lat, lng: hit.lng, placeName: hit.label })}
                >
                  <span className="who-mark">
                    <Icon size={20}>
                      <IconPin />
                    </Icon>
                  </span>
                  <span className="who-text">
                    <span className="who-name">{hit.label}</span>
                    <span className="who-meta">
                      {hit.kind ?? ""} {hit.lat.toFixed(4)}, {hit.lng.toFixed(4)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {hits !== null && hits.length === 0 && !searchFailed ? (
            <p className="field-note" role="status">
              {t("capture.locationSearchEmpty")}
            </p>
          ) : null}
          {attribution ? <p className="field-note">{attribution}</p> : null}
        </div>
      ) : null}

      {mode === "places" ? (
        <div className="picker-panel">
          <input
            className="input"
            name="place"
            value={placeQuery}
            placeholder={t("capture.locationPlacesPlaceholder")}
            onChange={(event) => setPlaceQuery(event.target.value)}
          />
          <ul className="who-list">
            {filteredPlaces.map((place) => (
              <li key={place.id}>
                <button
                  type="button"
                  className="who-row"
                  onClick={() =>
                    onChange({ lat: place.lat, lng: place.lng, placeName: place.placeName })
                  }
                >
                  <span className="who-mark">
                    <Icon size={20}>
                      <IconMap />
                    </Icon>
                  </span>
                  <span className="who-text">
                    <span className="who-name">{place.placeName ?? t("map.placeTitle")}</span>
                    <span className="who-meta">
                      {t("map.travellerCount")} {place.travellerCount}{" "}
                      {formatRelative(place.lastMomentAt, locale)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {places !== null && filteredPlaces.length === 0 ? (
            <p className="field-note" role="status">
              {t("capture.locationPlacesEmpty")}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="picker-state">
        {value ? (
          <>
            <span className="picker-value">
              <Icon size={16} color="var(--color-text-muted)">
                <IconCheck />
              </Icon>
              {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
            </span>
            <button type="button" className="btn btn-quiet" onClick={() => onChange(null)}>
              {t("capture.locationClear")}
            </button>
          </>
        ) : (
          <span className="picker-value">
            <Icon size={16} color="var(--color-text-muted)">
              <IconGlobe />
            </Icon>
            {t("capture.locationNone")}
          </span>
        )}
      </div>
    </div>
  );
}
